import React, { useState } from 'react';
import { API_BASE_URL } from '../../config/api';

interface RegistrationSuccessPageProps {
  email: string;
  onBackToLogin: () => void;
}

export const RegistrationSuccessPage: React.FC<RegistrationSuccessPageProps> = ({ 
  email, 
  onBackToLogin 
}) => {
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);

  const handleResendVerification = async () => {
    try {
      setIsResending(true);
      setResendError(null);
      setResendMessage(null);

      // 使用统一的API配置
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/resend-verification-by-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setResendMessage('验证邮件已重新发送！请检查您的邮箱。');
      } else {
        if (response.status === 429) {
          setResendError('发送太频繁，请稍后再试。');
        } else {
          setResendError(data.error?.message || '发送失败，请稍后重试。');
        }
      }
    } catch (error) {
      setResendError('网络错误，请检查连接后重试。');
    } finally {
      setIsResending(false);
    }
  };

  const handleContinueToLogin = () => {
    // 使用传入的onBackToLogin回调而不是navigate
    onBackToLogin();
  };

  return (
    <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
      <div className="text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        </div>
        
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          注册成功！
        </h2>
        
        <p className="text-gray-600 mb-4">
          我们已向 <span className="font-medium text-blue-600">{email}</span> 发送了验证邮件。
        </p>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                请验证您的邮箱
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>
                  点击邮件中的验证链接以激活您的账户。验证后您将能够使用所有功能。
                </p>
                <p className="mt-1">
                  如果没收到邮件，请检查垃圾邮件箱。
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="space-y-3">
          <button
            type="button"
            onClick={handleResendVerification}
            disabled={isResending}
            className="w-full bg-gray-100 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isResending ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                重新发送中...
              </span>
            ) : (
              '重新发送验证邮件'
            )}
          </button>
          
          <button
            onClick={handleContinueToLogin}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
          >
            返回登录页面
          </button>
        </div>
        
        {resendMessage && (
          <div className="mt-4 text-sm text-green-700 bg-green-50 p-3 rounded-md">
            {resendMessage}
          </div>
        )}
        
        {resendError && (
          <div className="mt-4 text-sm text-red-700 bg-red-50 p-3 rounded-md">
            {resendError}
          </div>
        )}
      </div>
    </div>
  );
}; 